import classNames from 'classnames';
import { useI18n } from '@/libraries/contexts/i18n-context';
import React from 'react';
import Icon from '@/components/atoms/icon/icon';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import Link from '@/components/utilities/link/link';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import styles from './recent-search-card.module.scss';

export interface RecentSearchCardProps {
  /** The class name to apply */
  className?: string;
  /** Duration information of the recent search */
  durationText?: string;
  /** Headline of the card */
  headline?: string[] | string;
  /** Indicates whether to display the loading state */
  isLoading?: boolean;
  /** An event that is triggered when clicking the marker */
  onClick?: () => void;
  /** Pax information of the recent search */
  paxText?: string;
  /** Transport method information of the recent search */
  transportMode?: string;
}

const RecentSearchCard = ({
  className,
  durationText,
  headline,
  isLoading = false,
  onClick,
  paxText,
  transportMode,
}: RecentSearchCardProps) => {
  const { t } = useI18n();
  const isLaptop = useWindowSize('laptop');

  const renderHeadline = () => {
    if (typeof headline === 'string') {
      return (
        <Typography className={styles.headline} tagStyle="headingSmall">
          {headline}
        </Typography>
      );
    }

    if (!Array.isArray(headline) || headline.length === 0) {
      return null;
    }

    const firstItem = headline[0];
    const remainingCount = headline.length - 1;

    return (
      <Typography className={styles.headline} tagStyle="headingSmall">
        {remainingCount > 0
          ? `${t('items.common.words.to')} ${firstItem}, +${remainingCount}...`
          : `${t('items.common.words.to')} ${firstItem}`}
      </Typography>
    );
  };

  if (isLoading) {
    const height = isLaptop ? '22px' : '16px';

    return (
      <div className={styles.component}>
        <SkeletonLoader borderRadius="4px" height={height} width="60%" />

        <SkeletonLoader borderRadius="4px" height={height} width="80%" />

        <SkeletonLoader borderRadius="4px" height={height} width="50%" />
      </div>
    );
  }

  return (
    <Link className={classNames(styles.component, className)} onClick={onClick}>
      {headline && renderHeadline()}

      {(durationText || paxText || transportMode) && (
        <div className={styles.details}>
          {durationText && (
            <div className={styles.duration}>
              <Icon className={styles.icon} name="Calendar" size={16} />

              <Typography className={styles.text} tagStyle="bodyMedium">
                {durationText}
              </Typography>
            </div>
          )}

          {paxText && transportMode && (
            <div className={styles.pax}>
              <Icon className={styles.icon} name="User" size={16} />

              <Typography className={styles.text} tagStyle="bodyMedium">
                {paxText}, {transportMode}
              </Typography>
            </div>
          )}
        </div>
      )}
    </Link>
  );
};

RecentSearchCard.displayName = 'RecentSearchCard';

export default RecentSearchCard;
